<template>
  <MeterGroup v-bind.prop="props" :pt="passThrough" :key="props.orientation">
    <template
      v-for="name in Object.keys(slots)"
      :key="name"
      v-slot:[name]="slotData"
    >
      <slot :name="name" v-bind="slotData || {}"></slot>
    </template>
  </MeterGroup>
</template>

<script setup lang="ts">
import { MeridiaMeterGroupProps } from './types';
import { MeterGroupPassThroughOptions } from 'primevue';
import { PassThrough } from '@primevue/core';
import { usePassThrough } from 'primevue/passthrough';
import MeterGroup, { MeterGroupSlots } from 'primevue/metergroup';
import { ref, watch } from 'vue';

const props = withDefaults(defineProps<MeridiaMeterGroupProps>(), {
  orientation: 'horizontal',
  min: 0,
  max: 100,
  labelPosition: 'end',
  labelOrientation: 'horizontal',
});

const slots = defineSlots<MeterGroupSlots>();
const passThrough = ref<PassThrough<MeterGroupPassThroughOptions>>();

updatePassThrough();
watch(props, () => {
  updatePassThrough();
});

function updatePassThrough() {
  passThrough.value = usePassThrough(
    props.pt,
    {
      root: {
        class: {
          'flex-col': props.orientation === 'horizontal',
          'flex-row': props.orientation === 'vertical',
          'flex gap-xs': true,
        },
      },
      meters: {
        class: {
          'flex-col h-full': props.orientation === 'vertical',
          'inline-flex bg-surface-200 rounded-md gap-[1px]': true,
        },
        style: {
          width:
            props.orientation === 'vertical'
              ? props.width
                ? props.width
                : '8px'
              : '100%',
          height:
            props.orientation === 'horizontal'
              ? props.width
                ? props.width
                : '8px'
              : '100%',
        },
      },
      meter: {
        class: {
          'rounded-s-md rounded-e-md h-full':
            props.orientation === 'horizontal',
          'rounded-ss-md rounded-se-md rounded-ee-md rounded-es-md w-full':
            props.orientation === 'vertical',
          'transition-all duration-300 ease-in-out': true,
        },
      },
      labelList: {
        class: {
          'flex flex-wrap m-0 p-0 list-none': true,
          'gap-m': props.orientation === 'horizontal',
          'flex-col gap-2xs items-start': props.orientation === 'vertical',
        },
      },
      label: {
        class: {
          'inline-flex items-center gap-2xs text-caption-small text-neutral-text-subtitle':
            true,
        },
      },
      labelIcon: {
        class: {
          'text-base w-m h-m': true,
        },
      },
    } as MeterGroupPassThroughOptions,
    { mergeSections: false, mergeProps: true }
  ) as PassThrough<MeterGroupPassThroughOptions>;
}
</script>

<style lang="scss"></style>
